import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { FlaskConical, AlertTriangle, Shield, Plus, Download, Info } from 'lucide-react';

function HeavyMetals() {
  const { API } = useContext(AppContext);
  const [metalsData, setMetalsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetalsData();
  }, []);

  const fetchMetalsData = async () => {
    // Mock data
    setMetalsData([
      { id: '1', name: 'Lead (Pb)', code: 'HM-PB-001', quantity: 4500, unit: 'kg', purity: 99.5, storageLocation: 'Heavy Metal Bay A', hazardLevel: 'High', lastInspection: '2025-01-28', complianceStatus: 'Compliant' },
      { id: '2', name: 'Cadmium (Cd)', code: 'HM-CD-001', quantity: 850, unit: 'kg', purity: 99.9, storageLocation: 'Heavy Metal Bay B', hazardLevel: 'Very High', lastInspection: '2025-01-25', complianceStatus: 'Compliant' },
      { id: '3', name: 'Mercury (Hg)', code: 'HM-HG-001', quantity: 120, unit: 'kg', purity: 99.99, storageLocation: 'Secure Vault', hazardLevel: 'Extreme', lastInspection: '2025-02-01', complianceStatus: 'Under Review' },
      { id: '4', name: 'Chromium (Cr)', code: 'HM-CR-001', quantity: 2200, unit: 'kg', purity: 98.5, storageLocation: 'Heavy Metal Bay A', hazardLevel: 'Medium', lastInspection: '2025-01-30', complianceStatus: 'Compliant' },
      { id: '5', name: 'Nickel (Ni)', code: 'HM-NI-001', quantity: 3100, unit: 'kg', purity: 99.0, storageLocation: 'Heavy Metal Bay C', hazardLevel: 'Medium', lastInspection: '2025-01-29', complianceStatus: 'Compliant' },
    ]);
    setLoading(false);
  };

  const getHazardColor = (level) => {
    switch (level) {
      case 'Extreme': return 'bg-red-600 text-white';
      case 'Very High': return 'bg-red-100 text-red-700 border border-red-200';
      case 'High': return 'bg-orange-100 text-orange-700 border border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'Compliant': return 'bg-green-100 text-green-700';
      case 'Under Review': return 'bg-blue-100 text-blue-700';
      case 'Non-Compliant': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const totalQuantity = metalsData.reduce((sum, d) => sum + d.quantity, 0);

  if (loading) {
    return <div className="text-center py-12">Loading heavy metals data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="heavy-metals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Heavy Metals</h1>
          <p className="text-gray-500 mt-1">Manage hazardous heavy metal inventory</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-gray-50">
            <Download size={18} />
            <span>Export</span>
          </button>
          <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
            <Plus size={18} />
            <span>Add Entry</span>
          </button>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="text-red-500 mt-0.5" size={24} />
          <div>
            <h4 className="font-semibold text-red-800">Safety Notice</h4>
            <p className="text-sm text-red-700 mt-1">Heavy metals require special handling procedures. Ensure all safety protocols are followed and PPE is worn when accessing storage areas.</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-5 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Total Stock</p>
              <p className="text-2xl font-bold text-purple-700">{(totalQuantity / 1000).toFixed(2)} MT</p>
            </div>
            <FlaskConical className="text-purple-500" size={32} />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Compliant</p>
              <p className="text-2xl font-bold text-green-700">{metalsData.filter(m => m.complianceStatus === 'Compliant').length}</p>
            </div>
            <Shield className="text-green-500" size={32} />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Under Review</p>
          <p className="text-2xl font-bold text-blue-700">{metalsData.filter(m => m.complianceStatus === 'Under Review').length}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <p className="text-sm text-red-600 font-medium">High Hazard Items</p>
          <p className="text-2xl font-bold text-red-700">{metalsData.filter(m => ['High', 'Very High', 'Extreme'].includes(m.hazardLevel)).length}</p>
        </div>
      </div>

      {/* Metals Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Metal</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Purity</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Hazard Level</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Compliance</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Last Inspection</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {metalsData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <FlaskConical className="text-purple-500" size={18} />
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e]">{item.code}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">{item.quantity.toLocaleString()} {item.unit}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.purity}%</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.storageLocation}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getHazardColor(item.hazardLevel)}`}>
                      {item.hazardLevel}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getComplianceColor(item.complianceStatus)}`}>
                      {item.complianceStatus}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500">{item.lastInspection}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default HeavyMetals;
